import type {
  DomainCanvas,
  DomainDocument,
  EntityId,
  JsonValue,
  KnowledgeItem,
  SaveReceipt,
} from "@note-app/contracts";
import {
  migrateCanvasToCurrent,
  migrateDocumentToCurrent,
  type Clock,
  type RevisionEntityType,
  type RevisionOperation,
} from "@note-app/domain";
import {
  EntityNotFoundError,
  OptimisticConcurrencyError,
  type UnitOfWork,
} from "@note-app/storage";

export interface RevisionSummary {
  readonly id: EntityId;
  readonly itemId: EntityId;
  readonly entityType: RevisionEntityType;
  readonly operation: RevisionOperation;
  readonly revision: number;
  readonly createdAt: number;
}

export interface StructuredDiffEntry {
  readonly path: string;
  readonly kind: "added" | "removed" | "changed";
  readonly before?: JsonValue;
  readonly after?: JsonValue;
}

export interface RevisionPreview {
  readonly revision: RevisionSummary;
  readonly snapshot: JsonValue;
  readonly currentRevision: number;
  readonly diff: readonly StructuredDiffEntry[];
  readonly truncated: boolean;
}

export interface RevisionRestoreResult {
  readonly item: KnowledgeItem;
  readonly receipt: SaveReceipt;
  readonly safetyRevision: number;
  readonly restoredFromRevision: number;
}

const PAGE_SIZE = 200;
const MAX_DIFF_ENTRIES = 200;

export class RevisionApplicationService {
  public constructor(
    private readonly unitOfWork: UnitOfWork,
    private readonly clock: Clock,
  ) {}

  public async list(itemId: EntityId): Promise<readonly RevisionSummary[]> {
    const revisions = [];
    let cursor: string | undefined;
    do {
      const page = await this.unitOfWork.repositories.revisions.list(itemId, {
        limit: PAGE_SIZE,
        ...(cursor === undefined ? {} : { cursor }),
      });
      revisions.push(...page.items.map(toSummary));
      cursor = page.nextCursor;
    } while (cursor !== undefined);
    return revisions;
  }

  public async preview(input: {
    itemId: EntityId;
    revisionId: EntityId;
  }): Promise<RevisionPreview> {
    const [item, revision] = await Promise.all([
      this.unitOfWork.repositories.items.get(input.itemId),
      this.unitOfWork.repositories.revisions.get(input.revisionId),
    ]);
    if (item === null) {
      throw new EntityNotFoundError("item", input.itemId);
    }
    if (revision === null || revision.itemId !== input.itemId) {
      throw new EntityNotFoundError("revision", input.revisionId);
    }
    const current = await loadCurrentSnapshot(this.unitOfWork, item);
    const entries: StructuredDiffEntry[] = [];
    diffJson(current.snapshot, revision.snapshot, "$", entries);
    return {
      revision: toSummary(revision),
      snapshot: revision.snapshot,
      currentRevision: current.revision,
      diff: entries.slice(0, MAX_DIFF_ENTRIES),
      truncated: entries.length > MAX_DIFF_ENTRIES,
    };
  }

  public restore(input: {
    itemId: EntityId;
    revisionId: EntityId;
    expectedRevision: number;
  }): Promise<RevisionRestoreResult> {
    return this.unitOfWork.transaction(async (repositories) => {
      const item = await repositories.items.get(input.itemId);
      if (item === null) {
        throw new EntityNotFoundError("item", input.itemId);
      }
      if (item.revision !== input.expectedRevision) {
        throw new OptimisticConcurrencyError(
          item.id,
          input.expectedRevision,
          item.revision,
        );
      }
      const target = await repositories.revisions.get(input.revisionId);
      if (target === null || target.itemId !== item.id) {
        throw new EntityNotFoundError("revision", input.revisionId);
      }
      if (target.entityType !== item.kind) {
        throw new EntityNotFoundError(`${item.kind} revision`, input.revisionId);
      }
      const committedAt = Math.max(this.clock.now(), item.updatedAt);
      let safetyRevision: number;
      let receipt: SaveReceipt;
      if (item.kind === "document") {
        const current = await repositories.documents.load(item.id);
        if (current === null) {
          throw new EntityNotFoundError("document", item.id);
        }
        const restored = migrateDocumentToCurrent(
          target.snapshot as unknown as DomainDocument,
        ).value;
        const safety = await repositories.documents.save(
          item.id,
          current.document,
          input.expectedRevision,
          committedAt,
          { operation: "safety-snapshot" },
        );
        safetyRevision = safety.revision;
        receipt = await repositories.documents.save(
          item.id,
          restored,
          safety.revision,
          committedAt,
          { operation: "restore" },
        );
      } else {
        const current = await repositories.canvases.load(item.id);
        if (current === null) {
          throw new EntityNotFoundError("canvas", item.id);
        }
        const restored = migrateCanvasToCurrent(
          target.snapshot as unknown as DomainCanvas,
        ).value;
        const safety = await repositories.canvases.save(
          item.id,
          current.canvas,
          input.expectedRevision,
          committedAt,
          { operation: "safety-snapshot" },
        );
        safetyRevision = safety.revision;
        receipt = await repositories.canvases.save(
          item.id,
          restored,
          safety.revision,
          committedAt,
          { operation: "restore" },
        );
      }
      const restoredItem = await repositories.items.get(item.id);
      if (restoredItem === null) {
        throw new EntityNotFoundError("item", item.id);
      }
      return {
        item: restoredItem,
        receipt,
        safetyRevision,
        restoredFromRevision: target.revision,
      };
    });
  }
}

function toSummary(
  revision: import("@note-app/domain").RevisionRecord,
): RevisionSummary {
  return {
    id: revision.id,
    itemId: revision.itemId,
    entityType: revision.entityType,
    operation: revision.operation ?? "save",
    revision: revision.revision,
    createdAt: revision.createdAt,
  };
}

async function loadCurrentSnapshot(
  unitOfWork: UnitOfWork,
  item: KnowledgeItem,
): Promise<{ revision: number; snapshot: JsonValue }> {
  if (item.kind === "document") {
    const stored = await unitOfWork.repositories.documents.load(item.id);
    if (stored === null) throw new EntityNotFoundError("document", item.id);
    return {
      revision: stored.revision,
      snapshot: stored.document as unknown as JsonValue,
    };
  }
  const stored = await unitOfWork.repositories.canvases.load(item.id);
  if (stored === null) throw new EntityNotFoundError("canvas", item.id);
  return {
    revision: stored.revision,
    snapshot: stored.canvas as unknown as JsonValue,
  };
}

function diffJson(
  before: JsonValue | undefined,
  after: JsonValue | undefined,
  path: string,
  entries: StructuredDiffEntry[],
): void {
  if (entries.length > MAX_DIFF_ENTRIES) return;
  if (before === undefined) {
    entries.push({ path, kind: "added", ...(after === undefined ? {} : { after }) });
    return;
  }
  if (after === undefined) {
    entries.push({ path, kind: "removed", before });
    return;
  }
  if (JSON.stringify(before) === JSON.stringify(after)) return;
  if (Array.isArray(before) && Array.isArray(after)) {
    const length = Math.max(before.length, after.length);
    for (let index = 0; index < length; index += 1) {
      diffJson(before[index], after[index], `${path}[${index}]`, entries);
    }
    return;
  }
  if (isObject(before) && isObject(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    for (const key of [...keys].sort()) {
      diffJson(before[key], after[key], `${path}.${key}`, entries);
    }
    return;
  }
  entries.push({ path, kind: "changed", before, after });
}

function isObject(value: JsonValue): value is Record<string, JsonValue> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
